import React, {useEffect, useState} from 'react';
import styles from './calendar.module.css';

interface CalendarProps {
    onDateSelect: (date: Date | [Date, Date] | null) => void;
    allowRangeSelection?: boolean;
    initialDateRange?: [Date, Date] | null;
}

const Calendar: React.FC<CalendarProps> = ({ onDateSelect, allowRangeSelection = false, initialDateRange = null }) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [rangeStart, setRangeStart] = useState<Date | null>(null);
    const [rangeEnd, setRangeEnd] = useState<Date | null>(null);


    const weekdays = ['ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ', 'ВС'];
    const months = [
        'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
        'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
    ];

    useEffect(() => {
        if (initialDateRange) {
            setRangeStart(initialDateRange[0]);
            setRangeEnd(initialDateRange[1]);
        }
    }, [initialDateRange]);

    const getDaysInMonth = (date: Date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const firstDayOfMonth = new Date(year, month, 1).getDay();
        const days = [];

        for (let i = 1; i <= daysInMonth; i++) {
            days.push(new Date(year, month, i));
        }

        return { days, firstDayOfMonth };
    };

    const { days, firstDayOfMonth } = getDaysInMonth(currentDate);

    const handleDateClick = (date: Date) => {
        if (allowRangeSelection) {
            if (!rangeStart || (rangeStart && rangeEnd)) {
                setRangeStart(date);
                setRangeEnd(null);
            } else {
                setRangeEnd(date);
            }
        } else {
            setSelectedDate(date);
        }
    };

    const setEndOfDay = (date: Date): Date => {
        const endDate = new Date(date);
        endDate.setHours(23, 59, 59, 999);
        return endDate;
    };

    const handleApply = () => {
        if (allowRangeSelection) {
            if (rangeStart && rangeEnd) {
                onDateSelect([rangeStart, setEndOfDay(rangeEnd)]);
            } else if (rangeStart) {
                onDateSelect([rangeStart, setEndOfDay(rangeStart)]);
            }
        } else if (selectedDate) {
            onDateSelect(selectedDate);
        }
    };

    const handleMonthChange = (increment: number) => {
        const newDate = new Date(currentDate);
        newDate.setMonth(newDate.getMonth() + increment);
        setCurrentDate(newDate);
    };

    const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newDate = new Date(currentDate);
        newDate.setFullYear(parseInt(event.target.value));
        setCurrentDate(newDate);
    };

    const years = Array.from({ length: 10 }, (_, i) => currentDate.getFullYear() - 5 + i);

    return (
        <div className={styles.calendar}>
            <div className={styles.header}>
                <select
                    value={currentDate.getFullYear()}
                    onChange={handleYearChange}
                    className={styles.yearSelect}
                >
                    {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                    ))}
                </select>
                <div className={styles.monthNavigation}>
                    <button onClick={() => handleMonthChange(-1)} className={styles.navButton}>&lt;</button>
                    <span>{months[currentDate.getMonth()]}</span>
                    <button onClick={() => handleMonthChange(1)} className={styles.navButton}>&gt;</button>
                </div>
            </div>
            <div className={styles.weekdays}>
                {weekdays.map(day => (
                    <div key={day} className={styles.weekday}>{day}</div>
                ))}
            </div>
            <div className={styles.days}>
                {Array(firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1).fill(null).map((_, index) => (
                    <div key={`empty-${index}`} className={styles.emptyDay} />
                ))}
                {days.map(date => (
                    <div
                        key={date.toISOString()}
                        className={`${styles.day} ${
                            (selectedDate && date.toDateString() === selectedDate.toDateString()) ||
                            (rangeStart && date.toDateString() === rangeStart.toDateString()) ||
                            (rangeEnd && date.toDateString() === rangeEnd.toDateString())
                                ? styles.selected
                                : ''
                        }`}
                        onClick={() => handleDateClick(date)}
                    >
                        {date.getDate()}
                    </div>
                ))}
            </div>
            <button className={styles.applyButton} onClick={handleApply} disabled={!selectedDate && !rangeStart}>Применить</button>
        </div>
    );
};

export default Calendar;